import { CheckCircle2, FileText, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { MemoizedIframePreview } from '@/components/treatment/v3/request/package-view/MemoizedIframePreview';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import type {
  GetSubmissionAttemptDetailResponse,
} from '@/services/api/agentflowService';
import { useGetSubmissionAttemptDetailQuery } from '@/services/api/agentflowService';

import { ContactMethodSection } from './ContactMethodSection';
import { FieldsSection } from './FieldsSection';
import { PackageContentsSection } from './PackageContentsSection';
import { PipelineStepIndicator } from './PipelineStepIndicator';
import { SignatureSection } from './SignatureSection';
import { SubmissionAttemptActions } from './SubmissionAttemptActions';
import { SubmissionAttemptHeader } from './SubmissionAttemptHeader';
import {
  getSubmissionAttemptPresentationState,
  getSubmissionAttemptSummary,
} from './submissionAttemptPresentation';

function resolvePreviewUrl(pdfUrl: string | null): string | null {
  if (!pdfUrl) return null;
  try {
    return new URL(
      pdfUrl,
      import.meta.env.VITE_APP_DJANGO_BACKEND_HOST ?? window.location.origin,
    ).toString();
  } catch {
    return pdfUrl;
  }
}

const CHANNEL_PREFIXES: Record<string, string> = {
  fax: 'Fax to',
  email: 'Email to',
  mail: 'Mail to',
  portal: 'Portal',
};

function getSelectedContactMethodValue(
  detail: GetSubmissionAttemptDetailResponse | null | undefined,
): string | null {
  const contactMethod = detail?.contact_method ?? null;
  const activeContactMethod =
    contactMethod?.manual_contact_method ?? contactMethod?.recommended_contact_method;

  if (!activeContactMethod) {
    return null;
  }

  const prefix = CHANNEL_PREFIXES[activeContactMethod.channel] ?? '';
  return `${prefix} ${activeContactMethod.value ?? ''}`.trim();
}

interface SubmissionAttemptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenRequestModal: () => void;
  matterId: string;
  treatmentRequestId: string;
}

export const SubmissionAttemptModal = ({
  open,
  onOpenChange,
  onOpenRequestModal,
  matterId,
  treatmentRequestId,
}: SubmissionAttemptModalProps) => {
  const [submissionPhase, setSubmissionPhase] = useState<'idle' | 'submitting'>('idle');
  const [submissionQueued, setSubmissionQueued] = useState(false);
  const shouldPollSubmissionState =
    open && (submissionPhase === 'submitting' || submissionQueued);
  const {
    data: detail,
    error,
    isLoading,
    refetch,
  } = useGetSubmissionAttemptDetailQuery(
    { matterId, requestId: treatmentRequestId },
    {
      skip: !open,
      refetchOnMountOrArgChange: true,
      pollingInterval: shouldPollSubmissionState ? 2000 : undefined,
    },
  );

  useEffect(() => {
    if (!open) {
      setSubmissionPhase('idle');
      setSubmissionQueued(false);
    }
  }, [open]);

  useEffect(() => {
    if (detail?.status === 'submitting') {
      setSubmissionQueued(true);
      return;
    }

    if (detail?.status === 'submitted') {
      setSubmissionQueued(false);
      return;
    }

    if (detail != null) {
      setSubmissionQueued(false);
    }
  }, [detail]);

  const activeDetail = detail;
  const previewUrl = resolvePreviewUrl(activeDetail?.pdf_url ?? null);
  const pageCount = activeDetail?.package_contents.reduce(
    (sum, c) => sum + (c.page_count ?? 0),
    0,
  );
  const hasError = Boolean(error) && !detail;
  const summary = activeDetail ? getSubmissionAttemptSummary(activeDetail) : null;
  const presentationState = activeDetail
    ? getSubmissionAttemptPresentationState(activeDetail)
    : null;
  const isSubmittingFlow = submissionPhase !== 'idle';
  const isSubmitted = activeDetail?.status === 'submitted';
  const isSubmissionInFlight =
    !isSubmitted &&
    (submissionPhase === 'submitting' ||
      submissionQueued ||
      activeDetail?.status === 'submitting');
  const selectedContactMethodValue = getSelectedContactMethodValue(activeDetail);
  const headerContent = (() => {
    if (activeDetail) {
      return (
        <SubmissionAttemptHeader
          detail={activeDetail}
          submissionPhase={submissionPhase}
          onClose={() => onOpenChange(false)}
        />
      );
    }

    if (hasError) {
      return (
        <div className="flex items-center justify-between gap-3 border-b border-zinc-200 px-6 py-3 shrink-0">
          <span className="text-sm font-medium text-zinc-900">
            Submission Attempt Unavailable
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 border-b border-zinc-200 px-6 py-3 shrink-0">
        <Skeleton className="h-3 w-3 rounded-full" />
        <Skeleton className="h-5 w-48" />
      </div>
    );
  })();
  const leftPanelContent = (() => {
    if (hasError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm font-medium text-red-800">
            Failed to load submission attempt data.
          </p>
          <p className="mt-1 text-xs text-red-700">
            Retry, or open the manual submission flow if you only need
            to continue sending the request.
          </p>
        </div>
      );
    }

    if (isLoading || !detail) {
      return (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      );
    }

    if (isSubmitted && activeDetail) {
      return (
        <div className="flex min-h-full items-center justify-center py-6">
          <div className="w-full max-w-xl rounded-[28px] border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-sky-50 p-7 shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>

            <div className="mt-5 text-center">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                Submitted
              </div>
              <h3 className="mt-2 text-2xl font-semibold tracking-tight text-zinc-950">
                Package submitted
              </h3>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-zinc-600">
                The HIPAA package was submitted successfully and the request has been moved into
                Confirm Receipt.
              </p>
            </div>

            <div className="mt-6 rounded-full bg-emerald-100/80 p-1">
              <div className="h-2 rounded-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-sky-400" />
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/80 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                  Delivery
                </div>
                <div className="mt-2 text-base font-medium text-zinc-900">
                  {selectedContactMethodValue ?? 'Submission method recorded'}
                </div>
              </div>

              <div className="rounded-2xl border border-white/80 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                  Next Step
                </div>
                <div className="mt-2 text-base font-medium text-zinc-900">
                  Confirm Receipt
                </div>
                <p className="mt-1 text-sm leading-relaxed text-zinc-600">
                  The request will now continue through follow-up and receipt confirmation.
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-dashed border-emerald-200 bg-white/70 px-4 py-3 text-center">
              <p className="text-sm leading-relaxed text-zinc-600">
                Transmission updates will continue in the background. You can close this review
                whenever you’re ready.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        {detail.pipeline_steps.length > 0 && (
          <PipelineStepIndicator
            steps={detail.pipeline_steps}
            summaryTitle={summary?.title}
          />
        )}

        <ContactMethodSection
          contactMethod={detail.contact_method}
          matterId={matterId}
          requestId={treatmentRequestId}
        />

        <SignatureSection
          signature={detail.signature}
          matterId={matterId}
          requestId={treatmentRequestId}
        />

        <PackageContentsSection
          packageContents={detail.package_contents}
        />

        <FieldsSection
          fields={detail.fields}
          matterId={matterId}
          requestId={treatmentRequestId}
        />
      </>
    );
  })();
  const previewPanel = (() => {
    if (hasError) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-lg bg-zinc-100 px-6 text-center">
          <p className="text-sm font-medium text-zinc-700">
            Unable to load the submission attempt.
          </p>
          <p className="text-xs text-zinc-500">
            The backend has an attempt for this request, but this view did not
            load it successfully.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetch();
            }}
          >
            Retry
          </Button>
        </div>
      );
    }

    if (isLoading || !activeDetail) {
      return <Skeleton className="flex-1 rounded-lg" />;
    }

    if (!previewUrl) {
      return (
        <div className="flex flex-1 items-center justify-center bg-zinc-100 rounded-lg">
          <span className="text-sm text-zinc-500">
            No PDF generated yet
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        <div className="flex items-center gap-2 mb-2 px-1 shrink-0">
          <FileText className="h-4 w-4 text-zinc-500" />
          <span className="text-sm font-medium text-zinc-700">
            Draft PDF
          </span>
          {pageCount != null && pageCount > 0 && (
            <span className="text-xs text-zinc-500">
              {pageCount} page{pageCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {presentationState?.reviewBlocked && summary?.title && !isSubmitted && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
            <p className="text-xs font-medium text-amber-800">{summary.title}</p>
            {summary.description && (
              <p className="mt-1 text-xs leading-relaxed text-amber-700">
                {summary.description}
              </p>
            )}
          </div>
        )}
        <MemoizedIframePreview
          url={previewUrl}
          title="Draft PDF Preview"
          assumePdf
        />
      </div>
    );
  })();

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (isSubmittingFlow && !nextOpen) {
          return;
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent hideCloseButton className="flex h-[92vh] min-h-0 max-w-[96vw] flex-col p-0 overflow-hidden">
        {/* Header */}
        {headerContent}

        {/* Body: split panel */}
        <div className="flex flex-1 min-h-0">
          {/* Left panel */}
          <div className="flex w-[720px] shrink-0 flex-col border-r border-zinc-200">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {isSubmissionInFlight && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600 shrink-0" />
                    <div>
                      <div className="text-sm font-semibold text-blue-900">
                        Submitting package
                      </div>
                      <div className="text-xs text-blue-700">
                        Waiting for the HIPAA package to be submitted and the request to move into Confirm Receipt.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {leftPanelContent}
            </div>

            {/* Actions footer */}
            {detail && !isSubmitted && (
              <SubmissionAttemptActions
                detail={detail}
                matterId={matterId}
                requestId={treatmentRequestId}
                submissionPhase={submissionPhase}
                onSubmissionPhaseChange={setSubmissionPhase}
                onClose={() => onOpenChange(false)}
                onSubmitted={() => {
                  setSubmissionQueued(true);
                  refetch();
                }}
                onOpenRequestModal={onOpenRequestModal}
              />
            )}
          </div>

          {/* Right panel: PDF preview */}
          <div className="flex flex-1 flex-col min-h-0 min-w-0 p-3">
            {previewPanel}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
